// Views/JobDetail.jsx
import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaMapMarkerAlt,
  FaClock,
  FaBriefcase,
  FaArrowLeft,
  FaCheckCircle,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaFileUpload,
  FaExclamationCircle,
} from "react-icons/fa";
import Jobs from "../Views/jobsData";

const JobDetail = () => {
  const { jobSlug } = useParams();
  const navigate = useNavigate();
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    experience: "",
    resume: null,
    coverLetter: "",
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Find the job from the data
  const job = Jobs.find((j) => j.slug === jobSlug);
  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Job Not Found</h2>
          <button
            onClick={() => navigate("/careers")}
            className="text-black hover:underline font-semibold"
          >
            Back to Careers
          </button>
        </div>
      </div>
    );
  }

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (submitError) setSubmitError(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    // Validate file size (5MB max)
    if (file && file.size > 5 * 1024 * 1024) {
      setSubmitError("Resume file size must be less than 5MB");
      e.target.value = null;
      return;
    }

    setFormData((prev) => ({
      ...prev,
      resume: file,
    }));
    if (submitError) setSubmitError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Create FormData object for multipart/form-data
      const formDataToSend = new FormData();
      formDataToSend.append("full_name", formData.fullName);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("experience", formData.experience);
      formDataToSend.append("resume", formData.resume);

      // Cover letter is optional
      if (formData.coverLetter.trim()) {
        formDataToSend.append("cover_letter", formData.coverLetter);
      }

      // Make API call
      const response = await fetch(
        "https://api3.made2tech.com/api/v1/nexonetics/career",
        {
          method: "POST",
          body: formDataToSend,
          // Don't set Content-Type header - browser will set it automatically with boundary
        }
      );

      if (!response.ok) {
        throw new Error(`Application submission failed: ${response.status}`);
      }

      const data = await response.json();
      console.log("Application submitted successfully:", data);

      // Show success message
      setFormSubmitted(true);

      // Reset form after 3 seconds
      setTimeout(() => {
        setShowApplicationForm(false);
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          experience: "",
          resume: null,
          coverLetter: "",
        });
        setFormSubmitted(false);
        setIsSubmitting(false);
      }, 3000);
    } catch (error) {
      console.error("Error submitting application:", error);
      setSubmitError(
        "Failed to submit application. Please try again or contact support."
      );
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Black Theme */}
      <div className="bg-black text-white py-20">
        <div className="container mx-auto px-6">
          <button
            onClick={() => navigate("/careers")}
            className="flex items-center gap-2 mb-6 hover:text-gray-300 transition-colors duration-300"
          >
            <FaArrowLeft /> Back to Careers
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{job.title}</h1>

            <div className="flex flex-wrap gap-6 text-lg">
              <div className="flex items-center gap-2">
                <FaMapMarkerAlt />
                <span>{job.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <FaBriefcase />
                <span>{job.department}</span>
              </div>
              <div className="flex items-center gap-2">
                <FaClock />
                <span>{job.type}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-200"
          >
            <h2 className="text-2xl font-bold mb-4 text-black">
              About the Role
            </h2>
            <p className="text-gray-700 leading-relaxed mb-8">
              {job.description}
            </p>

            {job.experience && (
              <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="text-lg font-semibold mb-2 text-black">
                  Experience Required
                </h3>
                <p className="text-gray-700">{job.experience}</p>
              </div>
            )}

            {job.salary && (
              <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="text-lg font-semibold mb-2 text-black">
                  Salary Range
                </h3>
                <p className="text-gray-700">{job.salary}</p>
              </div>
            )}

            <h3 className="text-xl font-bold mb-4 text-black">
              Key Responsibilities
            </h3>
            <ul className="space-y-3 mb-8">
              {job.responsibilities.map((resp, index) => (
                <li key={index} className="flex items-start gap-3">
                  <FaCheckCircle className="text-black mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{resp}</span>
                </li>
              ))}
            </ul>

            <h3 className="text-xl font-bold mb-4 text-black">Requirements</h3>
            <ul className="space-y-3 mb-8">
              {job.requirements.map((req, index) => (
                <li key={index} className="flex items-start gap-3">
                  <FaCheckCircle className="text-gray-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{req}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => setShowApplicationForm(true)}
              className="w-full bg-black text-white py-4 rounded-xl font-semibold hover:bg-gray-800 transition-all duration-300"
            >
              Apply for this Position
            </button>
          </motion.div>
        </div>
      </div>

      {/* Application Form Modal */}
      {showApplicationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            {!formSubmitted ? (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-black">
                    Apply for {job.title}
                  </h3>
                  <button
                    onClick={() => {
                      setShowApplicationForm(false);
                      setSubmitError(null);
                    }}
                    className="text-gray-500 hover:text-black text-2xl"
                  >
                    ×
                  </button>
                </div>

                {/* Error Message */}
                {submitError && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <FaExclamationCircle className="text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="text-red-700">{submitError}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="relative">
                    <FaUser className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="fullName"
                      placeholder="Full Name"
                      required
                      value={formData.fullName}
                      onChange={handleFormChange}
                      disabled={isSubmitting}
                      className="text-gray-700 w-full pl-12 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-black focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>
                  <div className="relative">
                    <FaEnvelope className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      placeholder="Email"
                      required
                      value={formData.email}
                      onChange={handleFormChange}
                      disabled={isSubmitting}
                      className="w-full text-gray-700  pl-12 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-black focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>
                  <div className="relative">
                    <FaPhone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      required
                      value={formData.phone}
                      onChange={handleFormChange}
                      disabled={isSubmitting}
                      className="w-full pl-12 text-gray-700  pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-black focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>
                  <div className="relative">
                    <FaBriefcase className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="experience"
                      placeholder="Years of Experience"
                      required
                      value={formData.experience}
                      onChange={handleFormChange}
                      disabled={isSubmitting}
                      className="w-full pl-12 text-gray-700  pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-black focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block  mb-2 font-semibold text-black  items-center gap-2">
                      <FaFileUpload />
                      Resume / CV
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      required
                      onChange={handleFileChange}
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 rounded-lg border text-gray-800 border-gray-300 focus:ring-2 focus:ring-black focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Accepted formats: PDF, DOC, DOCX (Max 5MB)
                    </p>
                  </div>
                  <textarea
                    name="coverLetter"
                    placeholder="Cover Letter (Optional)"
                    value={formData.coverLetter}
                    onChange={handleFormChange}
                    disabled={isSubmitting}
                    rows="4"
                    className="w-full text-gray-700  px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-black focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <div className="flex gap-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-black text-white py-3 rounded-lg hover:bg-gray-800 font-semibold transition-all duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? "Submitting..." : "Submit Application"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowApplicationForm(false);
                        setSubmitError(null);
                      }}
                      disabled={isSubmitting}
                      className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 font-semibold transition-all duration-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="text-center py-8">
                <FaCheckCircle className="text-black text-6xl mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2 text-black">
                  Application Submitted!
                </h3>
                <p className="text-gray-600">
                  Thank you for applying. We'll review your application and get
                  back to you soon.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default JobDetail;
